<template>
  <!-- Import Modal -->
  <ImportModal
    v-if="!shouldHideImport && isImportModalOpen"
    import-type="custom_data"
    import-mode="file"
    :params="{ idCustomData: customObjectId }"
    :title="t('myShopData.import.importModal.customData.title')"
    :subtitle="t('myShopData.import.importModal.customData.subtitle')"
    @close="isImportModalOpen = false"
  />

  <div class="custom-objects-page">
    <!-- Page Header Section -->
    <div
      v-if="!shouldHidePageHeader"
      class="page-header"
    >
      <!-- Back link -->
      <router-link
        v-if="!shouldHideBackLink"
        :to="{ name: 'shop.objects-data-list' }"
        class="back-link"
      >
        <i class="fas fa-arrow-left" />
        <span>{{ t('navbar.customData') }}</span>
      </router-link>

      <!-- Title and actions row -->
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">
            {{ customDateName }}
          </h1>
          <p
            v-if="customDataDescription"
            class="page-summary"
          >
            {{ customDataDescription }}
          </p>
        </div>
        <div class="header-actions">
          <Button
            v-if="!shouldHideImport"
            class="p-button p-button-primary"
            @click="toggleImportModal"
          >
            {{ t('myShopData.button.importCustomData') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <SpmTableAPI
        v-if="apiCall && columns.length > 0"
        :api-call="apiCall"
        :show-reload-button="true"
        :show-filters-sort-button="!shouldHideFilters"
        :persistent-filters="persistentFilters"
        :columns="columns"
        :data-key="primaryKeys"
      >
        <template #cell-__meta_created_at="{ data }">
          <SpmDateDisplay
            class="custom-object-date"
            :value="data.__meta_created_at"
            format="datetime"
            size="sm"
          />
        </template>

        <template #cell-__meta_updated_at="{ data }">
          <SpmDateDisplay
            class="custom-object-date"
            :value="data.__meta_updated_at"
            format="datetime"
            size="sm"
          />
        </template>

        <template
          v-for="relationColumn in relationColumns"
          :key="`relation-${relationColumn.id}`"
          #[`cell-${relationColumn.id}`]="{ data }"
        >
          <div class="relation-cell">
            <span class="relation-cell__value">
              {{ formatRelationValue(data[relationColumn.id]) }}
            </span>
            <RelationPopoverCard
              v-if="hasRelationValue(data[relationColumn.id])"
              :relation="relationColumn.relation"
              :relation-value="formatRelationValue(data[relationColumn.id])"
              :custom-schemas="customSchemasCatalog"
            />
          </div>
        </template>
      </SpmTableAPI>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Tooltip from 'primevue/tooltip';
import { useRoute, useRouter } from 'vue-router';

import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import RelationPopoverCard from '@/components/custom-data/RelationPopoverCard.vue';
import ImportModal from '@/components/import/ImportModal.vue';
import type {
  SpmColumnConfig, ApiCallConfig, SpmFieldConfig, SpmFilterType, SpmApiFilter,
} from '@/components/table-rest-api/types';
import { NestApiType } from '@/composables/nestApi';
import { fetchCustomSchemaDefinition, fetchCustomSchemas } from '@/composables/custom-data/customData';
import type { SchemaItem } from '@/composables/custom-data/customData';
import type { CustomObjectRelationDefinition } from '@/composables/custom-data/relationResolver';
import { getSystemRelationSchemaDefinition } from '@/composables/custom-data/systemRelationSchemas';
import { showToastError } from '@/helpers';

interface CustomObjectsContext {
  mode?: 'standalone' | 'embedded' | string;
  relation?: {
    target?: 'contact' | string;
    id?: string | number;
    sourceField?: string;
  };
  ui?: {
    showHeader?: boolean;
    showBackLink?: boolean;
    showImport?: boolean;
    showFilters?: boolean;
  };
}

export default defineComponent({
  name: 'CustomObjects',
  components: {
    Button,
    SpmTableAPI,
    SpmDateDisplay,
    RelationPopoverCard,
    ImportModal,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    customObjectId: {
      type: [String, Number],
      required: false,
      default: '',
    },
    context: {
      type: Object as PropType<CustomObjectsContext | null>,
      required: false,
      default: null,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();

    const customObjectId = ref(String(props.customObjectId || route.query.id || ''));
    const contextMode = computed(() => String(
      props.context?.mode
      || route.query.contextMode
      || '',
    ).toLowerCase());
    const isContactContext = computed(() => (
      props.context?.relation?.target === 'contact' || contextMode.value === 'contact'
    ));
    const shouldHideImport = computed(() => {
      if (props.context?.ui?.showImport !== undefined) return !props.context.ui.showImport;
      return route.query.hideImport === '1' || isContactContext.value;
    });
    const shouldHideFilters = computed(() => {
      if (props.context?.ui?.showFilters !== undefined) return !props.context.ui.showFilters;
      return route.query.hideFilters === '1' || isContactContext.value;
    });
    const shouldHideBackLink = computed(() => {
      if (props.context?.ui?.showBackLink !== undefined) return !props.context.ui.showBackLink;
      return contextMode.value === 'embedded';
    });
    const shouldHidePageHeader = computed(() => {
      if (props.context?.ui?.showHeader !== undefined) return !props.context.ui.showHeader;
      return false;
    });
    const isEmbedded = computed(() => contextMode.value === 'embedded');
    const contactId = computed(() => Number(
      props.context?.relation?.id
      || route.query.idShopContact,
    ));
    const contactSourceFieldFromRoute = computed(() => String(
      props.context?.relation?.sourceField
      || route.query.sourceField
      || '',
    ));
    const persistentFilters = ref<SpmApiFilter[]>([]);

    const customDateName = ref<string>('');
    const customDataDescription = ref<string>('');

    const apiCall = ref<ApiCallConfig | null>(null);

    const columns = ref<SpmColumnConfig[]>([]);
    const primaryKeys = ref<string | string[]>('id');
    const relationBySourceField = ref<Record<string, CustomObjectRelationDefinition>>({});
    const customSchemasCatalog = ref<SchemaItem[]>([]);

    const isImportModalOpen = ref(false);

    const relationColumns = computed(() => columns.value
      .map((column) => {
        const relation = relationBySourceField.value[column.id];
        if (!relation) return null;
        return {
          id: column.id,
          relation,
        };
      })
      .filter((entry): entry is { id: string; relation: CustomObjectRelationDefinition } => !!entry));

    const SYSTEM_FILTER_FIELDS: Record<string, string[]> = {
      contacts: [
        'contact_first_name',
        'contact_last_name',
        'created_at',
        'updated_at',
        'contact_data_email',
        'contact_data_sms',
        'contact_country',
        'contact_lang',
      ],
      products: ['reference', 'name', 'link', 'price'],
    };

    const CUSTOM_META_FILTER_FIELDS = ['__meta_created_at', '__meta_updated_at', 'created_at', 'updated_at'];

    const toLabel = (value: string): string => value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

    const getRelationValue = (value: unknown): string | number | undefined => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? undefined : trimmed;
      }
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
      }
      return undefined;
    };

    const hasRelationValue = (value: unknown): boolean => getRelationValue(value) !== undefined;

    const formatRelationValue = (value: unknown): string | number => {
      const relationValue = getRelationValue(value);
      return relationValue === undefined ? '-' : relationValue;
    };

    /**
     * Map custom data field types to filter types
     */
    const getFilterTypeForDataType = (dataType: string): SpmFilterType => {
      switch (dataType) {
        case 'number':
        case 'decimal':
          return 'numberInput';
        case 'datetime':
        case 'date':
          return 'dateRange';
        case 'boolean':
          return 'singleSelect';
        case 'list':
          return 'singleSelect';
        default:
          return 'textInput';
      }
    };

    const getTranslatedSystemFieldLabel = (fieldName: string): string => {
      if (fieldName === 'contact_first_name') return t('myLists.manage.customers.headers.firstname');
      if (fieldName === 'contact_last_name') return t('myLists.manage.customers.headers.lastname');
      if (fieldName === 'created_at') return t('myLists.manage.customers.headers.creationDate');
      if (fieldName === 'updated_at') return t('myLists.manage.customers.headers.modifDate');
      if (fieldName === 'contact_data_email') return t('myLists.manage.customers.headers.email');
      if (fieldName === 'contact_data_sms') return t('myLists.manage.customers.headers.sms');
      if (fieldName === 'contact_country') return t('myLists.manage.customers.headers.country');
      if (fieldName === 'contact_lang') return t('myLists.manage.customers.headers.lang');
      if (fieldName === 'reference') return t('myShopData.products.headers.reference');
      if (fieldName === 'name') return t('myShopData.products.headers.name');
      if (fieldName === 'link') return t('myShopData.products.headers.link');
      if (fieldName === 'price') return t('myShopData.products.headers.price');
      return toLabel(fieldName);
    };

    const getSystemFilterType = (schemaName: string, fieldName: string): SpmFilterType => {
      if (schemaName === 'contacts') {
        if (fieldName === 'created_at' || fieldName === 'updated_at') return 'dateRange';
        if (fieldName === 'contact_country') return 'countrySelect';
        return 'textInput';
      }
      if (schemaName === 'products') {
        if (fieldName === 'price') return 'numberInput';
      }
      return 'textInput';
    };

    const resolveCustomTargetSchema = (
      relation: CustomObjectRelationDefinition,
      catalog: SchemaItem[],
    ): SchemaItem | undefined => {
      const targetSchema = String(relation.targetSchema);
      return catalog.find((schema) => String(schema.id_custom_object) === targetSchema
        || schema.table_name === targetSchema
        || schema.schema_name === targetSchema);
    };

    const buildSystemRelationFilterFields = (
      sourceField: string,
      relation: CustomObjectRelationDefinition,
      sourceLabel: string,
    ): SpmFieldConfig[] => {
      const definition = getSystemRelationSchemaDefinition(relation.targetSchema);
      const allowedFields = SYSTEM_FILTER_FIELDS[relation.targetSchema]
        || definition?.preferredPreviewFields
        || [];

      return allowedFields.map((targetField) => ({
        name: `${sourceField}__rel__${targetField}`,
        label: `${sourceLabel} - ${getTranslatedSystemFieldLabel(targetField)}`,
        filterable: true,
        filterConfig: {
          type: getSystemFilterType(relation.targetSchema, targetField),
        },
        relationFilterConfig: {
          sourceField,
          targetSchemaType: relation.targetSchemaType,
          targetSchema: String(relation.targetSchema),
          targetJoinField: relation.targetField,
          targetFilterField: targetField,
        },
      }));
    };

    const buildCustomRelationFilterFields = (
      sourceField: string,
      relation: CustomObjectRelationDefinition,
      sourceLabel: string,
      catalog: SchemaItem[],
    ): SpmFieldConfig[] => {
      const targetSchema = resolveCustomTargetSchema(relation, catalog);
      const targetFields = Array.isArray(targetSchema?.fields) ? targetSchema!.fields : [];

      const mainFields = targetFields
        .filter((field: any) => field?.name)
        .filter((field: any) => {
          const fieldName = String(field.name);
          if (fieldName === relation.targetField) return false;
          if (fieldName.startsWith('__meta_')) return false;
          if (/(^id$|^id_|_id$)/i.test(fieldName)) return false;
          return true;
        })
        .slice(0, 4);

      const selectedFields = [
        ...mainFields,
        ...targetFields.filter((field: any) => CUSTOM_META_FILTER_FIELDS.includes(field?.name)),
      ];

      return selectedFields.map((field: any) => ({
        name: `${sourceField}__rel__${field.name}`,
        label: `${sourceLabel} - ${field.label || toLabel(field.name)}`,
        filterable: true,
        filterConfig: {
          type: getFilterTypeForDataType(field.type || 'text'),
        },
        relationFilterConfig: {
          sourceField,
          targetSchemaType: relation.targetSchemaType,
          targetSchema: String(relation.targetSchema),
          targetJoinField: relation.targetField,
          targetFilterField: field.name,
        },
      }));
    };

    onMounted(async () => {
      if (!customObjectId.value) {
        showToastError(t('customData.listing.definition_not_found'));
        if (!isEmbedded.value) {
          router.push({ name: 'shop.data' });
        }
        return;
      }

      // Initialiser l'objet apiCall
      apiCall.value = {
        type_api: NestApiType.CLIENT_API,
        path: `/custom-data-records/${customObjectId.value}`,
        secure: true,
      };
      const customDataDefinition = await fetchCustomSchemaDefinition(parseInt(customObjectId.value, 10));
      customSchemasCatalog.value = await fetchCustomSchemas();
      if (customDataDefinition && customDataDefinition.success && customDataDefinition.data) {
        const customDataDefinitionData = customDataDefinition.data;
        customDateName.value = customDataDefinitionData.name;
        customDataDescription.value = customDataDefinitionData.description || '';

        relationBySourceField.value = (customDataDefinitionData.relationships || []).reduce(
          (
            accumulator: Record<string, CustomObjectRelationDefinition>,
            relationship: any,
          ) => {
            if (!relationship?.sourceField) return accumulator;
            accumulator[relationship.sourceField] = {
              sourceField: relationship.sourceField,
              targetSchemaType: relationship.targetSchemaType,
              targetSchema: String(relationship.targetSchema),
              targetField: relationship.targetField,
            };
            return accumulator;
          },
          {},
        );

        if (isContactContext.value && Number.isFinite(contactId.value) && contactId.value > 0) {
          let sourceField = contactSourceFieldFromRoute.value;
          if (!sourceField) {
            const contactRelationship = (customDataDefinitionData.relationships || []).find((relationship: any) => relationship?.targetSchemaType === 'system'
              && String(relationship?.targetSchema) === 'contacts'
              && relationship?.sourceField);
            sourceField = contactRelationship?.sourceField || '';
          }

          if (sourceField) {
            persistentFilters.value = [{
              field: sourceField,
              operator: 'EQUALS',
              values: [contactId.value],
            }];
          }
        }

        // Get unique keys - can be single or composite
        const pks = customDataDefinitionData.unique_keys;
        if (pks && pks.length > 0) {
          primaryKeys.value = pks.length === 1 ? pks[0] : pks;
        }

        // Build columns: start with system auto-increment ID
        const builtColumns: SpmColumnConfig[] = [{
          id: 'id',
          header: 'ID',
          fields: [{
            name: 'id',
            label: 'ID',
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'numberInput',
            },
          }],
          maxWidth: '80px',
        }];

        // Add user-defined fields
        customDataDefinitionData.fields.forEach((field: any) => {
          const sourceField = field.name;
          const sourceLabel = field.label || field.name;
          const relation = relationBySourceField.value[sourceField];

          builtColumns.push({
            id: sourceField,
            header: sourceLabel,
            fields: [{
              name: sourceField,
              label: sourceLabel,
              sortable: true,
              filterable: true,
              filterConfig: {
                type: getFilterTypeForDataType(field.type),
              },
            }],
          });

          if (!relation) return;

          const relationFilterFields = relation.targetSchemaType === 'system'
            ? buildSystemRelationFilterFields(sourceField, relation, sourceLabel)
            : buildCustomRelationFilterFields(
              sourceField,
              relation,
              sourceLabel,
              customSchemasCatalog.value,
            );

          relationFilterFields.forEach((relationFieldConfig) => {
            builtColumns.push({
              id: relationFieldConfig.name,
              header: relationFieldConfig.label || relationFieldConfig.name,
              fields: [relationFieldConfig],
              hidden: true,
            });
          });
        });

        // Add meta columns
        builtColumns.push({
          id: '__meta_created_at',
          header: t('customData.listing.default_fields_name.created_at'),
          fields: [{
            name: '__meta_created_at',
            label: t('customData.listing.default_fields_name.created_at'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'dateRange',
            },
          }],
        });
        builtColumns.push({
          id: '__meta_updated_at',
          header: t('customData.listing.default_fields_name.updated_at'),
          fields: [{
            name: '__meta_updated_at',
            label: t('customData.listing.default_fields_name.updated_at'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'dateRange',
            },
          }],
        });

        columns.value = builtColumns;
      } else {
        showToastError(t('customData.listing.definition_not_found'));
        if (!isEmbedded.value) {
          router.push({ name: 'shop.data' });
        }
      }
    });

    const toggleImportModal = () => {
      isImportModalOpen.value = !isImportModalOpen.value;
    };

    return {
      t,
      customDateName,
      customDataDescription,
      customObjectId,
      columns,
      relationColumns,
      customSchemasCatalog,
      apiCall,
      primaryKeys,
      shouldHideImport,
      shouldHideFilters,
      shouldHideBackLink,
      shouldHidePageHeader,
      persistentFilters,
      isImportModalOpen,
      toggleImportModal,
      hasRelationValue,
      formatRelationValue,
      getRelationValue,
    };
  },
});
</script>

<style scoped lang="scss">
/* Page layout */
.custom-objects-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Page Header */
.page-header {
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 0.75rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--secondary-700);
  }

  i {
    font-size: 0.75rem;
  }
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.page-summary {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin: 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
  line-height: 1.2;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Page Content */
.page-content {
  flex: 1;
}

.relation-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.relation-cell__value {
  color: var(--text-color);
  font-weight: 500;
}

:deep(.custom-object-date.spm-date-display) {
  color: var(--text-color);
}
</style>
